import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { Card, Checkbox, Form, Input, message, Popconfirm, Switch } from "antd";
import { Button } from "antd/lib/radio";
import moment from "moment-timezone";
import { Fragment, React, useEffect, useState } from "react";
import { useParams } from "react-router";
import { AddCustomTaskDrawer } from "../../../../Components/Drawers/AddCustomTask";
import AssignRequiredAttachment from "../../../../Components/Drawers/AssignRequiredAttachment/AssignRequiredAttachment";
import TaskQrCodeModal from "../../../../Components/Drawers/TaskQrCode/TaskQrCodeModal";
import { Loader } from "../../../../Components/Loader";
import { UpgradePlanModal } from "../../../../Components/Modals/UpgradePlanModal";
import { CustIcon } from "../../../../Components/Svgs";
import { ASSIGN_TASK, DELETE_CUSTOM_TASK } from "../../../../Graphql/mutations";
import { GET_USER_NAME, GET_USER_TIMEZONE } from "../../../../Graphql/queries";
import {
  GET_AVAILABLE_PREDEFINED_TASKS_FOR_USERS,
  GET_CUSTOM_TASKS_FOR_USERS,
  GET_USERS_ASSIGNED_TASKS,
} from "../../../../Graphql/subscriptions";
import { usePermissionUser } from "../../../../Utils/Context/permissionContext";
import "./EmployeeManageTasks.less";

const { Search } = Input;

export const EmployeeManageTasksPage = () => {
  const [form] = Form.useForm();
  const { employee_id } = useParams();
  const { userPermissionAccess: permission } = usePermissionUser();
  const [filterValue, setFilterValue] = useState(null);
  const [showCustomTaskDrawer, setShowCustomTaskDrawer] = useState(false);
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  const [customTaskEditId, setCustomTaskEditId] = useState("");
  const [currentDateTimeZone, setCurrentDateTimeZone] = useState(
    moment().tz("America/Los_Angeles").format("YYYY-MM-DD")
  );
  const [roleDisplayTasks, setRoleDisplayTasks] = useState([]);
  const [customDisplayTasks, setCustomDisplayTasks] = useState([]);
  const [userTaskId, setUserTaskId] = useState();
  const [showQrDrawer, setShowQrDrawer] = useState(false);
  const [showRequiredAttachmentDrawer, setShowRequiredAttachmentDrawer] =
    useState(false);
  const [requiredAttachmentList, setRequiredAttachmentList] = useState([]);
  const [checkedRequiredAttechTaskList, setCheckedRequiredAttechTaskList] =
    useState([]);
  const [selectedCustomTaskList, setSelectedCustomTaskList] = useState([]);

  const { data: userData, loading: userDataLoading } = useQuery(
    GET_USER_TIMEZONE,
    { variables: { user_id: employee_id } }
  );

  const { data: userNameData } = useQuery(GET_USER_NAME, {
    variables: { user_id: employee_id },
  });

  const { data: roleTasks, loading: roleTasksLoading } = useSubscription(
    GET_AVAILABLE_PREDEFINED_TASKS_FOR_USERS,
    {
      variables: {
        user_id: employee_id,
        filter: !!filterValue ? `%${filterValue}%` : undefined,
      },
      shouldResubscribe: true,
    }
  );
  const { data: customTasksList, loading: customTasksLoading } =
    useSubscription(GET_CUSTOM_TASKS_FOR_USERS, {
      variables: {
        user_id: employee_id,
        filter: !!filterValue ? `%${filterValue}%` : undefined,
      },
      shouldResubscribe: true,
    });

  const { data: assignedTasks, loading: assignedTasksLoading } =
    useSubscription(GET_USERS_ASSIGNED_TASKS, {
      variables: {
        user_id: employee_id,
        todays_date: currentDateTimeZone,
      },
      shouldResubscribe: true,
      skip: userDataLoading,
    });

  const [UPDATE_ASSIGNED_TASKS_MUTATION, { loading: isAssigningTask }] =
    useMutation(ASSIGN_TASK);

  const [DELETE_CUSTOM_TASKS_MUTATION, { loading: isDeletingTask }] =
    useMutation(DELETE_CUSTOM_TASK);

  useEffect(() => {
    if (userData) {
      setCurrentDateTimeZone(
        moment().tz(userData.getUsersById.time_zone).format("YYYY-MM-DD")
      );
    }
  }, [userData]);

  useEffect(() => {
    if (roleTasks && assignedTasks && customTasksList) {
      let assignedTasksId = assignedTasks.user_tasks_map.flatMap(({ task }) => {
        return [task.task_id];
      });
      form.setFieldsValue({
        assignedTasks: assignedTasksId,
      });
      setRoleDisplayTasks([
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "time_sensitive" &&
            assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "reminder" && assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "during_day" && assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "end_of_day" && assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "time_sensitive" &&
            !assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "reminder" && !assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "during_day" && !assignedTasksId.includes(p.task_id)
        ),
        ...roleTasks.tasks.filter(
          (p) =>
            p.task_type === "end_of_day" && !assignedTasksId.includes(p.task_id)
        ),
      ]);
      setCustomDisplayTasks([
        ...customTasksList.customTasks.filter(
          (p) =>
            p.task.task_type === "time_sensitive" && p.task_status === "pending"
        ),
        ...customTasksList.customTasks.filter(
          (p) => p.task.task_type === "reminder" && p.task_status === "pending"
        ),
        ...customTasksList.customTasks.filter(
          (p) =>
            p.task.task_type === "during_day" && p.task_status === "pending"
        ),
        ...customTasksList.customTasks.filter(
          (p) =>
            p.task.task_type === "end_of_day" && p.task_status === "pending"
        ),
        // ...customTasksList.customTasks.filter((p) => p.task_status === "pending"),
      ]);
    }
  }, [roleTasks, assignedTasks, customTasksList]);
  const onDeleteTask = async (task_id) => {
    let updatedSelections = assignedTasks.user_tasks_map
      .filter((t) => t.task.task_id !== task_id)
      .flatMap(({ task, task_date }) => [
        {
          task_id: task.task_id,
          task_type: task.task_type,
          task_date: task_date,
        },
      ]);
    try {
      const response = await UPDATE_ASSIGNED_TASKS_MUTATION({
        variables: {
          tasks: updatedSelections,
          user_id: employee_id,
        },
      });
      if (response?.data?.assign_task?.status === "OK") {
        const deletetaskResponse = await DELETE_CUSTOM_TASKS_MUTATION({
          variables: {
            task_id: task_id,
          },
        });
        if (!deletetaskResponse.errors) {
          message.success("Custom Task Deleted Successfully!");
        }
      }
    } catch (error) {}
  };

  const onResetAssignedTasks = async () => {
    let currentSelectedTasks = form.getFieldValue("assignedTasks");

    let customTaskArray = customTasksList.customTasks
      .filter((a) => a.task_status === "pending")
      .flatMap((t) => {
        if (currentSelectedTasks.includes(t.task.task_id)) {
          return [
            {
              task_id: t.task.task_id,
              task_type: t.task.task_type,
              task_date: t.task_date,
              required_attachment: t.required_attachment,
            },
          ];
        }
      })
      .filter((a) => !!a);
    // form.setFieldsValue({ assignedTasks: customTaskArray });
    try {
      const response = await UPDATE_ASSIGNED_TASKS_MUTATION({
        variables: {
          tasks: [...customTaskArray],
          user_id: employee_id,
        },
      });
      if (response?.data?.assign_task?.status === "OK") {
        // if (values.reset) {
        message.success("Task(s) reset successfully!");
        // } else {
        //   message.success(response.data.assign_task?.message);
        // }
      }
    } catch (error) {}
  };
  const onTaskSave = async (values) => {
    if (values && roleTasks) {
    let sanattestDefinedTaskArry = roleTasks?.tasks
      .flatMap((t) => {
        if (values.assignedTasks.includes(t.task_id)) {
          return [
            {
              task_id: t.task_id,
              task_type: t.task_type,
              type_name: t.taskTypeByTaskType.type_name,
              description: t.description,
              required_attachment: !!t.user_tasks_maps.filter(
                (ra) => ra.required_attachment && ra.task_status === "pending"
              ).length,
            },
          ];
        }
      })
      .filter((a) => !!a);
    let customTaskArray = customTasksList?.customTasks
      .filter((e) => e.task_status === "pending")
      .flatMap((t) => {
        if (values.assignedTasks.includes(t.task.task_id)) {
          return [
            {
              task_id: t.task.task_id,
              task_type: t.task.task_type,
              task_date: t.task_date,
              required_attachment: t.required_attachment,
            },
          ];
        }
      })
      .filter((a) => !!a);
    let selectedTasks = [...sanattestDefinedTaskArry];
    await setSelectedCustomTaskList(customTaskArray);
    await setCheckedRequiredAttechTaskList(
      selectedTasks
        .filter((ts) => ts.required_attachment)
        .map((ts) => {
          return ts.task_id;
        })
    );
    await setRequiredAttachmentList(selectedTasks);
    await setShowRequiredAttachmentDrawer(true);
      }
  };
  return (
    <Fragment>
      <section className="manageTaskEmployee">
        <Form
          form={form}
          onFinish={onTaskSave}
          initialValues={{ assignedTasks: [] }}
        >
          <div className="secSubTitle pageSubTilte">
            <div className="stLeft">
              <h5 className="">
                Edit{" "}
                {userNameData?.getUsersById?.first_name
                  ? userNameData?.getUsersById?.first_name + "'s"
                  : ""}{" "}
                tasks
              </h5>
            </div>
            <div className="stRight">
              <ul className="p-0 list-inline d-flex align-items-center">
                <li>
                  <Search
                    onSearch={setFilterValue}
                    placeholder="Search Task.."
                  />
                </li>
                <li className="pl-3">
                  <span
                    className="text-secondary cursor-pointer"
                    onClick={onResetAssignedTasks}
                  >
                    Reset
                  </span>
                </li>
                <li>
                  <span className="px-3 text-muted">|</span>
                </li>
                <li>
                  <span
                    className={`text-primary cursor-pointer`}
                    // loading={isAssigningTask}
                    style={{ pointerEvents: isAssigningTask ? "none" : "auto" }}
                    onClick={() => form.submit()}
                  >
                    {isAssigningTask ? "Saving..." : "Save"}
                  </span>
                </li>
              </ul>
            </div>
          </div>
          {assignedTasksLoading || roleTasksLoading || customTasksLoading ? (
            <Loader className="employeeManagetaskListLoader" />
          ) : (
            <Form.Item name="assignedTasks">
              <Checkbox.Group className="w-100">
                <Card className="taskEditRow mb-4" bordered={false}>
                  <div className="secSubTitle">
                    <div className="stLeft">
                      <h5>Custom tasks</h5>
                    </div>
                  </div>
                  <div className="taskEditList">
                    <Card
                      onClick={() => {
                        if (
                          customTasksList?.customTasks.reduce(
                            (count, current) => {
                              return (
                                count +
                                (current.task_status === "pending" ? 1 : 0)
                              );
                            },
                            0
                          ) < permission.allow_custom_task
                        ) {
                          setCustomTaskEditId("");
                          setShowCustomTaskDrawer((c) => !c);
                        } else {
                          setShowUpgradePlanModal(true);
                        }
                      }}
                      className="addTask taskEditList_blk text-center cursorPointer font-weight-400 mb-3"
                    >
                      <CustIcon type="plus" className="mr-2" /> Add custom tasks
                    </Card>

                    {customDisplayTasks.map(
                      (
                        {
                          task: singleTask,
                          task_date,
                          user_task_id,
                          task_status,
                          required_attachment,
                        },
                        index
                      ) => {
                        return (
                          <Card key={index} className="taskEditList_blk">
                            <div className="d-flex mb-1">
                              <div className="ml-auto mt-n2 pr-3">
                                <span className="fz-12">
                                  {moment(task_date, "YYYY-MM-DD").format(
                                    "MM-DD-YYYY"
                                  )}
                                </span>
                                <span className="px-3 text-muted">|</span>
                                <span
                                  className="text-secondary cursor-pointer fz-12"
                                  onClick={() => {
                                    setCustomTaskEditId(singleTask.task_id);
                                    setShowCustomTaskDrawer((c) => !c);
                                  }}
                                >
                                  Edit
                                </span>
                                <span className="px-3 text-muted">|</span>
                                <Popconfirm
                                  title="Are you sure to delete this task?"
                                  onConfirm={() =>
                                    onDeleteTask(singleTask.task_id)
                                  }
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <span className="text-danger cursor-pointer fz-12">
                                    Delete
                                  </span>
                                </Popconfirm>
                              </div>
                              <div
                                className={`tagStatus ${singleTask.task_type}`}
                              >
                                {singleTask.taskTypeByTaskType.type_name}
                              </div>
                            </div>

                            <Checkbox value={singleTask.task_id} disabled>
                              {singleTask.description}
                            </Checkbox>
                            <div className=" d-flex justify-content-end align-items-center">
                              {!!user_task_id && task_status == "pending" && (
                                <div className="">
                                  <div
                                    className="viewDetails"
                                    onClick={() => {
                                      setUserTaskId(user_task_id);
                                      setShowQrDrawer(true);
                                    }}
                                  >
                                    View Details
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      }
                    )}
                  </div>
                </Card>
                {roleDisplayTasks.map((singleTask, index) => {
                  const getUserTaskId = singleTask?.user_tasks_maps.find(
                    (t) => t.task_status === "pending"
                  );
                  return (
                    <Card key={index} className="taskEditList_blk">
                      <div className=" d-flex justify-content-end">
                        <div className={`tagStatus ${singleTask.task_type}`}>
                          {singleTask.taskTypeByTaskType.type_name}
                        </div>
                      </div>
                      <Checkbox value={singleTask.task_id}>
                        {singleTask.description}
                      </Checkbox>

                      <div className=" d-flex justify-content-end align-items-center">
                        {/* <div className="requiredAttched">
                          <Switch
                            size="small"
                            // defaultChecked={singleTask.task_id}
                            defaultChecked={
                              singleTask.user_tasks_maps.filter(
                                (x) => x.task_status === "pending"
                              )[0]?.required_attachment ?? false
                            }
                          />
                          <small className="pl-2">
                            Picture Attestation Required?
                          </small>
                        </div> */}
                        {getUserTaskId?.user_task_id && (
                          <div className="pl-3">
                            <div
                              className="viewDetails"
                              onClick={() => {
                                setUserTaskId(getUserTaskId?.user_task_id);
                                setShowQrDrawer(true);
                              }}
                            >
                              View Details
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </Checkbox.Group>
            </Form.Item>
          )}
          <div style={{ float: "right" }}>
            <ul className="p-0 list-inline d-flex align-items-center">
              {/* <li>
                  <Search onSearch={setFilterValue} placeholder="Search.." />
                </li> */}
              <li className="pl-3">
                <span
                  className="text-secondary cursor-pointer"
                  onClick={onResetAssignedTasks}
                >
                  Reset
                </span>
              </li>
              <li>
                <span className="px-3 text-muted">|</span>
              </li>
              <li>
                <span
                  className={`text-primary cursor-pointer`}
                  // loading={isAssigningTask}
                  style={{ pointerEvents: isAssigningTask ? "none" : "auto" }}
                  onClick={() => form.submit()}
                >
                  {isAssigningTask ? "Saving..." : "Save"}
                </span>
              </li>
            </ul>
          </div>
        </Form>
      </section>
      {showCustomTaskDrawer && (
        <AddCustomTaskDrawer
          visible
          toggleDrawer={() => setShowCustomTaskDrawer((c) => !c)}
          customTaskEditId={customTaskEditId}
          user_id={employee_id}
          preAssignedTasks={assignedTasks}
        />
      )}

      {showQrDrawer && (
        <TaskQrCodeModal
          userPermission={permission.allow_qr_code}
          userTaskId={userTaskId}
          showQrDrawer={showQrDrawer}
          setShowQrDrawer={setShowQrDrawer}
        />
      )}
      {showRequiredAttachmentDrawer && (
        <AssignRequiredAttachment
          requiredAttachmentList={requiredAttachmentList}
          setRequiredAttachmentList={setRequiredAttachmentList}
          showRequiredAttachmentDrawer={showRequiredAttachmentDrawer}
          setShowRequiredAttachmentDrawer={setShowRequiredAttachmentDrawer}
          checkedRequiredAttechTaskList={checkedRequiredAttechTaskList}
          setCheckedRequiredAttechTaskList={setCheckedRequiredAttechTaskList}
          selectedCustomTaskList={selectedCustomTaskList}
          user_id={employee_id}
        />
      )}
      {showUpgradePlanModal && (
        <UpgradePlanModal
          showUpgradePlanModal={showUpgradePlanModal}
          setShowUpgradePlanModal={setShowUpgradePlanModal}
        />
      )}
    </Fragment>
  );
};
